import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Bell, Mail, Clock, Check, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

interface NotificationChannel {
  email: boolean;
  inApp: boolean;
}

interface NotificationPreferences {
  email_enabled: boolean;
  in_app_enabled: boolean;
  digest_frequency: "instant" | "daily" | "weekly";
  categories: {
    team_updates: NotificationChannel;
    session_updates: NotificationChannel;
    phase_changes: NotificationChannel;
    recommendations: NotificationChannel;
    mentions: NotificationChannel;
    comments: NotificationChannel;
    system: NotificationChannel;
  };
}

const defaultPreferences: NotificationPreferences = {
  email_enabled: true,
  in_app_enabled: true,
  digest_frequency: "daily",
  categories: {
    team_updates: { email: true, inApp: true },
    session_updates: { email: true, inApp: true },
    phase_changes: { email: true, inApp: true },
    recommendations: { email: true, inApp: true },
    mentions: { email: true, inApp: true },
    comments: { email: false, inApp: true },
    system: { email: true, inApp: true },
  },
};

const Notifications = () => {
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Don't redirect - ProtectedRoute handles this

    if (profile?.notification_preferences) {
      // Migrate old format to new format
      const prefs = profile.notification_preferences as any;
      const migratedCategories: any = {};
      
      if (prefs.categories) {
        Object.keys(prefs.categories).forEach(key => {
          if (typeof prefs.categories[key] === 'boolean') {
            // Old format: just boolean
            migratedCategories[key] = { 
              email: prefs.categories[key], 
              inApp: prefs.categories[key] 
            };
          } else {
            // New format: channel object
            migratedCategories[key] = prefs.categories[key];
          }
        });
      }

      setPreferences({
        ...defaultPreferences,
        ...prefs,
        categories: {
          ...defaultPreferences.categories,
          ...migratedCategories,
        },
      });
    }
  }, [profile]);

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ notification_preferences: preferences as any })
        .eq("id", user.id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast({
        title: "Gespeichert",
        description: "Deine Benachrichtigungseinstellungen wurden aktualisiert.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Einstellungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateCategory = (
    category: keyof NotificationPreferences["categories"], 
    channel: 'email' | 'inApp', 
    value: boolean
  ) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          [channel]: value,
        },
      },
    }));
  };

  if (authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null; // ProtectedRoute handles redirect
  }

  const categoryLabels = {
    team_updates: {
      title: "Team-Updates",
      description: "Neue Teammitglieder und Rollenänderungen",
    },
    session_updates: {
      title: "Session-Updates",
      description: "Neue Entscheidungen und Fortschritte",
    },
    phase_changes: {
      title: "Phasen-Wechsel",
      description: "Entscheidungen in neue Phasen übergehen",
    },
    recommendations: {
      title: "Empfehlungen",
      description: "Fertige Analysen und Empfehlungen",
    },
    mentions: {
      title: "Erwähnungen",
      description: "Wenn du in Kommentaren erwähnt wirst",
    },
    comments: {
      title: "Kommentare",
      description: "Neue Kommentare in deinen Entscheidungen",
    },
    system: {
      title: "System",
      description: "Wichtige System-Updates",
    },
  };

  return (
    <div className="max-w-4xl space-y-6 pb-20 md:pb-6">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle>Benachrichtigungskanäle</CardTitle>
          </div>
          <CardDescription>
            Aktiviere oder deaktiviere Benachrichtigungskanäle
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="email-enabled" className="text-base font-medium">
                  E-Mail-Benachrichtigungen
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Erhalte Benachrichtigungen per E-Mail
              </p>
            </div>
            <Switch
              id="email-enabled"
              checked={preferences.email_enabled}
              onCheckedChange={(checked) =>
                setPreferences(prev => ({ ...prev, email_enabled: checked }))
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="in-app-enabled" className="text-base font-medium">
                  In-App-Benachrichtigungen
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Erhalte Benachrichtigungen in der Anwendung
              </p>
            </div>
            <Switch
              id="in-app-enabled"
              checked={preferences.in_app_enabled}
              onCheckedChange={(checked) =>
                setPreferences(prev => ({ ...prev, in_app_enabled: checked }))
              }
            />
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="digest-frequency" className="text-base font-medium">
                E-Mail-Frequenz
              </Label>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Wie oft möchtest du E-Mail-Benachrichtigungen erhalten?
            </p>
            <Select
              value={preferences.digest_frequency}
              onValueChange={(value: "instant" | "daily" | "weekly") =>
                setPreferences(prev => ({ ...prev, digest_frequency: value }))
              }
              disabled={!preferences.email_enabled}
            >
              <SelectTrigger id="digest-frequency" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Sofort</span>
                    <span className="text-xs text-muted-foreground">
                      Erhalte Benachrichtigungen sofort
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="daily">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Täglich</span>
                    <span className="text-xs text-muted-foreground">
                      Tägliche Zusammenfassung um 9:00 Uhr
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="weekly">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Wöchentlich</span>
                    <span className="text-xs text-muted-foreground">
                      Wöchentliche Zusammenfassung jeden Montag
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg md:text-xl">Benachrichtigungs-Kategorien</CardTitle>
          <CardDescription>
            Wähle für jede Kategorie aus, ob du E-Mail- und/oder In-App-Benachrichtigungen erhalten möchtest
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {/* Desktop header */}
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-4 items-center pb-3 border-b border-border">
            <div className="text-sm font-medium text-muted-foreground">Kategorie</div>
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 justify-center min-w-[80px]">
              <Mail className="h-3.5 w-3.5" />
              E-Mail
            </div>
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 justify-center min-w-[80px]">
              <Bell className="h-3.5 w-3.5" />
              In-App
            </div>
          </div>

          {Object.entries(categoryLabels).map(([key, { title, description }]) => {
            const category = preferences.categories[key as keyof typeof preferences.categories];
            const isEmailActive = preferences.email_enabled && category.email;
            const isInAppActive = preferences.in_app_enabled && category.inApp;

            return (
              <div key={key} className="py-4 border-b border-border/50 last:border-0">
                {/* Mobile layout */}
                <div className="sm:hidden space-y-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">
                        {title}
                      </Label>
                      {(isEmailActive || isInAppActive) && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          Aktiv
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {description}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${key}-email-mobile`}
                        checked={category.email}
                        onCheckedChange={(checked) =>
                          updateCategory(key as keyof typeof preferences.categories, 'email', checked)
                        }
                        disabled={!preferences.email_enabled}
                      />
                      <Label htmlFor={`${key}-email-mobile`} className="text-xs text-muted-foreground">
                        E-Mail
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`${key}-inapp-mobile`}
                        checked={category.inApp}
                        onCheckedChange={(checked) =>
                          updateCategory(key as keyof typeof preferences.categories, 'inApp', checked)
                        }
                        disabled={!preferences.in_app_enabled}
                      />
                      <Label htmlFor={`${key}-inapp-mobile`} className="text-xs text-muted-foreground">
                        In-App
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden sm:grid grid-cols-[1fr_auto_auto] gap-4 items-center">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`${key}-label`} className="text-sm font-medium cursor-pointer">
                        {title}
                      </Label>
                      {(isEmailActive || isInAppActive) && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          Aktiv
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {description}
                    </p>
                  </div>
                  
                  <div className="flex justify-center min-w-[80px]">
                    <Switch
                      id={`${key}-email`}
                      checked={category.email}
                      onCheckedChange={(checked) =>
                        updateCategory(key as keyof typeof preferences.categories, 'email', checked)
                      }
                      disabled={!preferences.email_enabled}
                    />
                  </div>
                  
                  <div className="flex justify-center min-w-[80px]">
                    <Switch
                      id={`${key}-inapp`}
                      checked={category.inApp}
                      onCheckedChange={(checked) =>
                        updateCategory(key as keyof typeof preferences.categories, 'inApp', checked)
                      }
                      disabled={!preferences.in_app_enabled}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Sticky save button on mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur border-t border-border sm:relative sm:p-0 sm:bg-transparent sm:backdrop-blur-none sm:border-0">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full sm:w-auto sm:min-w-[120px]"
        >
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Änderungen speichern
        </Button>
      </div>
    </div>
  );
};

export default Notifications;
